import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { Modal, Button, Spinner, EmptyState, ErrorState } from './ui';
import { useToast } from '../context/ToastContext';
import {
  getOrgNoteContent,
  getCanonicalNoteContent,
  saveOrgNoteContent,
  resolveImageSrc,
} from '../services/notes';

// Images embedded in markdown may point at Storage via gs:// or a
// firebasestorage URL; resolve those to a fetchable download URL. Ordinary
// http(s) images pass through unchanged. (Ported from prepgen-public.)
function MarkdownImage({ node, src, alt, ...props }) {
  const initial = typeof src === 'string' ? src : '';
  const [resolvedSrc, setResolvedSrc] = useState(initial);

  useEffect(() => {
    let cancelled = false;
    if (!initial) { setResolvedSrc(''); return undefined; }
    resolveImageSrc(initial)
      .then((url) => { if (!cancelled) setResolvedSrc(url); })
      .catch(() => { if (!cancelled) setResolvedSrc(initial); });
    return () => { cancelled = true; };
  }, [initial]);

  return <img className="notes-img" src={resolvedSrc} alt={alt || ''} loading="lazy" {...props} />;
}

// Per-topic notes editor/viewer for an institute's OWN copy of the notes.
// Mounted only when a topic is selected (parent gates rendering), mirroring the
// other b2b dialogs. Reads the org copy on open; when none exists yet, offers to
// seed from Prepgen's canonical notes or start blank.
export default function NotesViewer({ onClose, orgId, exam, section, topic }) {
  const toast = useToast();
  const examId = exam?.examId || exam?.id;
  const sectionId = section?.id;
  const topicId = topic?.id;
  const topicTitle = topic?.title || topic?.name || 'Topic';

  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null); // institute's saved markdown, or null if none yet
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (!orgId || !examId || !sectionId || !topicId) {
      setError('Missing topic information.');
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setIsEditing(false);
    getOrgNoteContent(orgId, examId, sectionId, topicId)
      .then((text) => { if (!cancelled) { setContent(text); setDraft(text || ''); } })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load notes.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, examId, sectionId, topicId]);

  // "Customize their own version": copy Prepgen's canonical notes into the editor
  // as a starting point. Nothing is written until the institute saves.
  const startFromCanonical = async () => {
    setSeeding(true);
    try {
      const canonical = await getCanonicalNoteContent(examId, sectionId, topicId);
      if (canonical == null) {
        toast.info('Prepgen has no notes for this topic yet — starting a blank note.');
        setDraft('');
      } else {
        setDraft(canonical);
        toast.success("Loaded Prepgen's notes — edit and save to create your copy.");
      }
      setIsEditing(true);
    } catch (err) {
      toast.error(err?.message || "Could not load Prepgen's notes.");
    } finally {
      setSeeding(false);
    }
  };

  const startBlank = () => { setDraft(''); setIsEditing(true); };

  const save = async () => {
    setSaving(true);
    try {
      await saveOrgNoteContent(orgId, examId, sectionId, topicId, draft);
      setContent(draft);
      setIsEditing(false);
      toast.success('Notes saved.');
    } catch (err) {
      toast.error(err?.message || 'Failed to save notes.');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setDraft(content || '');
    setIsEditing(false);
  };

  return (
    <Modal title={`Notes · ${topicTitle}`} onClose={onClose}>
      <div className="notes-viewer">
        {section?.title && <p className="muted notes-viewer-crumb">{section.title}</p>}

        {loading ? (
          <div className="page-loading"><Spinner /> Loading notes…</div>
        ) : error ? (
          <ErrorState message={error} />
        ) : isEditing ? (
          <>
            <textarea
              className="notes-editor"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write notes in Markdown. LaTeX math is supported with $…$ (inline) and $$…$$ (block)."
              autoFocus
            />
            <div className="modal-actions">
              <Button variant="secondary" onClick={cancelEdit} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save notes'}</Button>
            </div>
          </>
        ) : content == null ? (
          <EmptyState
            title="No custom notes yet"
            action={
              <div className="notes-empty-actions">
                <Button onClick={startFromCanonical} disabled={seeding}>
                  {seeding ? 'Loading…' : "Start from Prepgen's notes"}
                </Button>
                <Button variant="secondary" onClick={startBlank}>Create blank notes</Button>
                {/* Future (deferred): an AI "Generate notes" action slots in here,
                    writing to the same org path via the B2B backend rather than a
                    client-side MCP secret. */}
              </div>
            }
          >
            Create this institute's own version of the notes for this topic. Start from Prepgen's
            managed notes and adapt them, or write your own from scratch.
          </EmptyState>
        ) : (
          <>
            <div className="notes-toolbar">
              <Button variant="secondary" onClick={() => { setDraft(content); setIsEditing(true); }}>
                Edit notes
              </Button>
            </div>
            <div className="notes-body">
              <ReactMarkdown
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
                components={{ img: MarkdownImage }}
              >
                {content || ''}
              </ReactMarkdown>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
